import { ref } from 'vue';
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import { isNativeNotWeb } from '@/utils/platform-check';

/**
 * Tells the splash to show "Updating" with a progress bar when the app has just
 * come up on a newly installed bundle.
 *
 * ## Why this runs after the update, not during it
 *
 * The obvious design — draw a bar while the bundle downloads — cannot work.
 * The plugin checks and downloads when the process starts, before the webview
 * has run any JS, and with `autoUpdate: "onLaunch"` it applies and reloads as
 * soon as the download finishes. Measured on the simulator, a delta update
 * completed roughly a second after launch: the webview was still booting, so
 * there was no JS alive to draw anything, and the bundle that would have drawn
 * it was replaced mid-boot.
 *
 * Holding the splash from JS to make room for a bar is worse, not better. It
 * requires turning off `autoSplashscreen`, which is the native splash that
 * covers the reload — without it the apply shows a blank white frame, and the
 * bar still does not appear because the update has already landed.
 *
 * So the bar is shown on the *incoming* bundle's first paint. It is not a
 * simulation: an update genuinely did just install, and this is the app
 * finishing that work. What it costs is one short hold on the launch after an
 * update, and nothing at all on every other launch.
 *
 * ## Why a chain of updates shows the bar once
 *
 * Two bundles can land in a row — a device that takes `1.3.5.1`, and finds
 * `1.3.5.2` waiting on the channel by the time the new one has booted. Each
 * apply reloads the webview, so each one used to draw its own bar from zero and
 * the member read the pair as the app restarting rather than updating.
 *
 * The two applies are NOT seconds apart, which is what rules out a single bar
 * spanning them. `autoUpdate: "onLaunch"` permits an immediate apply once per
 * process, and the first update spends it; a bundle found afterwards is queued
 * and applies at the next background or launch, which may be minutes or hours
 * away (docs/ota-live-updates.md). Nothing this code can do holds a screen
 * across that, and holding the splash to wait for a reload that cannot come in
 * this process only delays the member.
 *
 * So the chain is remembered rather than animated. When a bundle lands and
 * another is already queued behind it, the version of that queued bundle is
 * written to localStorage; the launch that brings it up recognises itself as
 * the rest of an update the member has already watched, and skips the bar. One
 * "Updating" screen per chain, however many bundles the chain turns out to
 * hold.
 *
 * Keyed on the queued bundle's VERSION, not on elapsed time: only the bundle we
 * were actually told about may skip its bar, so an unrelated update days later
 * still announces itself. The time window is a backstop for a record whose
 * bundle never arrived, nothing more.
 *
 * Applying stays with the plugin. Nothing here installs, so a bundle can never
 * ship in a state that stops future updates arriving — the one-way failure
 * described in docs/ota-live-updates.md.
 */

/**
 * How long the bar is shown after an update lands.
 *
 * Overridable so a test build can slow it enough to photograph. The bar is
 * otherwise unobservable in a real OTA test: an update replaces the running
 * bundle, so the code that draws it is whatever CI published, not whatever is
 * on your branch.
 */
export const OTA_BEAT_MS = Number(process.env.VUE_APP_OTA_BEAT_MS) || 1500;

/**
 * How long a remembered chain stays valid.
 *
 * Generous because the queued bundle applies at the next background or launch,
 * not during this session — a member who closes the app on Friday should still
 * be spared the second bar on Monday. It can afford to be generous because the
 * record names the exact version it expects: a different bundle never matches
 * it, whatever the clock says. The window only reaps a record whose bundle
 * never arrived at all, so it cannot sit there indefinitely.
 */
const CHAIN_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Forces the bar on every launch. Test builds only — set
 * VUE_APP_OTA_FORCE_BEAT=true. Never set in CI: scripts/ci-env.sh writes only
 * the values it knows about, so this cannot reach a published bundle by
 * accident.
 */
const FORCE_BEAT = process.env.VUE_APP_OTA_FORCE_BEAT === 'true';

/** Frame interval while filling the bar. */
const TICK_MS = 60;

/** The bundle id seen on the previous launch, for detecting a change. */
export const OTA_LAST_BUNDLE_KEY = 'ota_last_seen_bundle';

/**
 * The version of the bundle a previous launch saw queued behind the one it was
 * showing the bar for, and when it saw it. The only thing that survives the
 * reload, so it is the only thing that can tell the launch after an update that
 * the member has already watched this one.
 */
export const OTA_CHAIN_KEY = 'ota_update_chain';

function readStored(key) {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStored(key, value) {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Private mode or a full quota. Only the beat is affected.
  }
}

function clearStored(key) {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // As above — nothing here is load-bearing.
  }
}

/**
 * True when the bundle now running is the one a previous launch told us was
 * queued — i.e. the member has already watched the bar for this round of
 * updates and should not be shown it again.
 *
 * A record that cannot be parsed, has expired, or names a different version
 * reads as no chain: the default is always to show the bar, because a missing
 * screen is a worse failure than a repeated one.
 */
function chainCovers(version) {
  const raw = readStored(OTA_CHAIN_KEY);
  if (!raw || !version) return false;

  let chain;
  try {
    chain = JSON.parse(raw);
  } catch {
    clearStored(OTA_CHAIN_KEY);
    return false;
  }

  const { expect, at } = chain ?? {};
  const fresh = Number.isFinite(at) && Date.now() - at < CHAIN_WINDOW_MS;
  if (!fresh) {
    clearStored(OTA_CHAIN_KEY);
    return false;
  }

  return expect === version;
}

function saveChain(expect) {
  writeStored(OTA_CHAIN_KEY, JSON.stringify({ expect, at: Date.now() }));
}

/** The version of the bundle this launch is running, or null if unreadable. */
async function runningVersion() {
  try {
    const current = await CapacitorUpdater.current();
    return current?.bundle?.version ?? null;
  } catch {
    return null;
  }
}

/**
 * The version of a bundle already queued behind the one that just landed, or
 * null when this is the end of the chain.
 *
 * Answering it costs one request to Capgo, and only on a launch that has just
 * updated. Any failure answers null: the next launch then shows a bar it could
 * have skipped, which is the safe way to be wrong.
 *
 * `getLatest()` reports what the CHANNEL is serving, not what this device will
 * accept — a bundle held off by a native-version floor or `--no-downgrade` is
 * named here forever. That costs nothing now the answer only writes a record:
 * the record names a version that never arrives, matches nothing, and is reaped
 * by the window.
 */
async function pendingUpdateVersion(running) {
  if (!isNativeNotWeb()) return null;

  try {
    const next = (await CapacitorUpdater.getLatest())?.version;
    return next && next !== running ? next : null;
  } catch {
    return null;
  }
}

export function useOtaUpdateGate() {
  const status = ref('idle');
  const percent = ref(0);

  /**
   * True when the running bundle differs from the one recorded last launch.
   *
   * State is compared rather than events listened for, deliberately: the apply
   * can happen before any JS exists, so an event-written flag would often never
   * be written at all.
   */
  async function bundleChangedSinceLastLaunch() {
    if (FORCE_BEAT) return true;
    if (!isNativeNotWeb()) return false;

    let id = null;
    try {
      const current = await CapacitorUpdater.current();
      id = current?.bundle?.id ?? null;
    } catch {
      return false;
    }
    if (!id) return false;

    const previous = readStored(OTA_LAST_BUNDLE_KEY);
    writeStored(OTA_LAST_BUNDLE_KEY, id);

    if (previous !== null) return previous !== id;

    // Nothing recorded yet. Running a live-update bundle still means one just
    // landed — either on a fresh install that updated immediately, or on the
    // first launch of a bundle whose predecessor had no record-keeping. Only
    // the built-in bundle means "no update has happened".
    return id !== 'builtin';
  }

  /** Fills the bar over OTA_BEAT_MS so it reads as progress, not a flash. */
  function fillBar() {
    return new Promise(resolve => {
      const started = Date.now();
      const timer = setInterval(() => {
        const elapsed = Date.now() - started;
        percent.value = Math.min(100, Math.round((elapsed / OTA_BEAT_MS) * 100));
        if (elapsed >= OTA_BEAT_MS) {
          clearInterval(timer);
          resolve();
        }
      }, TICK_MS);
    });
  }

  /**
   * App.vue drives this from a watcher, which can fire more than once. Without
   * memoising, a second call would restart the animation.
   */
  let pending = null;

  function run() {
    pending = pending ?? execute();
    return pending;
  }

  async function execute() {
    if (!(await bundleChangedSinceLastLaunch())) {
      // No update this launch, so nothing can still be queued from the last
      // one: whatever chain was recorded either finished or was abandoned.
      clearStored(OTA_CHAIN_KEY);
      return;
    }

    const running = await runningVersion();
    const alreadyShown = chainCovers(running);
    const queued = await pendingUpdateVersion(running);

    // Re-arm or clear the record BEFORE the bar, not after. The bar is the only
    // part of this that takes time, and a launch interrupted during it must not
    // lose the fact that another bundle is still coming.
    if (queued) saveChain(queued);
    else clearStored(OTA_CHAIN_KEY);

    // The member watched this update's bar on the launch that queued this
    // bundle. Showing it again is the repetition this whole record exists to
    // prevent, so the splash lifts as it would on any ordinary launch.
    if (alreadyShown) return;

    status.value = 'updating';
    percent.value = 0;
    await fillBar();
    status.value = 'idle';
    percent.value = 0;
  }

  return { status, percent, run };
}
